// Frame picker for the event promo video thumbnail (platform event edit page).
// Play/scrub to any frame of the attached video, capture it to a canvas, and
// stage it as the event[video_thumbnail] file so it uploads with the normal
// form save.
//
// Source order matters for canvas capture (a cross-origin frame taints the
// canvas): try the same-origin Rails storage proxy first, then the CDN with
// CORS, then the CDN without CORS as a last resort so the picker at least
// plays (capture then fails with a clear message instead of a black box).

const READY_EVENTS = ['DOMContentLoaded', 'turbo:load', 'turbolinks:load']
const FRAME_STEP = 0.033
const METADATA_TIMEOUT_MS = 12000

function formatTime(seconds) {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0
  const minutes = Math.floor(safe / 60)
  const secs = (safe % 60).toFixed(1).padStart(4, '0')
  return `${minutes}:${secs}`
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(resolve, type, quality)
    } catch (error) {
      reject(error)
    }
  })
}

function setupPicker(root) {
  if (root.dataset.videoThumbnailBound === 'true') {
    return
  }
  root.dataset.videoThumbnailBound = 'true'

  const openButton = root.querySelector('[data-video-thumbnail-open]')
  const panel = root.querySelector('[data-video-thumbnail-panel]')
  const video = root.querySelector('[data-video-thumbnail-video]')
  const scrub = root.querySelector('[data-video-thumbnail-scrub]')
  const timeLabel = root.querySelector('[data-video-thumbnail-time]')
  const playButton = root.querySelector('[data-video-thumbnail-play]')
  const captureButton = root.querySelector('[data-video-thumbnail-capture]')
  const cancelButton = root.querySelector('[data-video-thumbnail-cancel]')
  const stepButtons = root.querySelectorAll('[data-video-thumbnail-step]')
  const input = root.querySelector('[data-video-thumbnail-input]')
  const preview = root.querySelector('[data-video-thumbnail-preview]')
  const emptyLabel = root.querySelector('[data-video-thumbnail-empty]')
  const status = root.querySelector('[data-video-thumbnail-status]')

  const proxySrc = root.dataset.videoThumbnailSrc
  const cdnSrc = root.dataset.videoThumbnailFallbackSrc
  const sources = []
  if (proxySrc) sources.push({ url: proxySrc, crossOrigin: null })
  if (cdnSrc && cdnSrc !== proxySrc) {
    sources.push({ url: cdnSrc, crossOrigin: 'anonymous' })
    sources.push({ url: cdnSrc, crossOrigin: null })
  }

  if (!openButton || !panel || !video || !scrub || !captureButton || !input || sources.length === 0) {
    return
  }

  let previewObjectUrl = null
  let sourceIndex = -1
  let metadataLoaded = false
  let watchdog = null

  function showStatus(message) {
    if (!status) return
    status.textContent = message || ''
    status.hidden = !message
  }

  function updateTimeLabel() {
    if (timeLabel) {
      timeLabel.textContent = formatTime(video.currentTime)
    }
  }

  function updatePlayButton() {
    if (playButton) {
      playButton.textContent = video.paused ? 'Play' : 'Pause'
    }
  }

  function clearWatchdog() {
    if (watchdog) {
      clearTimeout(watchdog)
      watchdog = null
    }
  }

  function loadSource(index) {
    const source = sources[index]
    if (!source) {
      clearWatchdog()
      showStatus('The video could not be loaded for frame capture. Upload a thumbnail image instead.')
      return
    }

    sourceIndex = index
    metadataLoaded = false

    if (source.crossOrigin) {
      video.setAttribute('crossorigin', source.crossOrigin)
    } else {
      video.removeAttribute('crossorigin')
    }
    video.src = source.url
    video.load()

    clearWatchdog()
    watchdog = setTimeout(() => {
      if (!metadataLoaded) {
        loadSource(sourceIndex + 1)
      }
    }, METADATA_TIMEOUT_MS)
  }

  video.addEventListener('error', () => {
    if (!metadataLoaded && sourceIndex >= 0) {
      loadSource(sourceIndex + 1)
    }
  })

  video.addEventListener('loadedmetadata', () => {
    metadataLoaded = true
    clearWatchdog()
    showStatus('')
    if (Number.isFinite(video.duration)) {
      scrub.max = String(video.duration)
      // Nudge off zero so the browser decodes and paints a frame — with
      // preload="metadata" alone the element stays black until a seek.
      if (video.currentTime === 0) {
        try {
          video.currentTime = 0.01
        } catch (_) {
          // Seeking can throw before data is ready; the frame will paint on play.
        }
      }
    } else {
      // Some files (e.g. screen/stream recordings) report Infinity until the
      // browser is forced to scan for the real duration via a far seek;
      // durationchange fires with the finite value afterwards.
      try {
        video.currentTime = 1e10
      } catch (_) {
        // If the far seek is rejected the file stays scrub-less but playable.
      }
    }
    updateTimeLabel()
  })

  video.addEventListener('durationchange', () => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      return
    }
    scrub.max = String(video.duration)
    // Return from the far-seek duration probe to the start.
    if (video.currentTime > video.duration - 0.05) {
      video.currentTime = 0.01
    }
  })

  video.addEventListener('timeupdate', () => {
    if (document.activeElement !== scrub) {
      scrub.value = String(video.currentTime)
    }
    updateTimeLabel()
  })

  video.addEventListener('play', updatePlayButton)
  video.addEventListener('pause', updatePlayButton)
  video.addEventListener('ended', updatePlayButton)

  scrub.addEventListener('input', () => {
    video.pause()
    video.currentTime = Number(scrub.value)
    updateTimeLabel()
  })

  stepButtons.forEach(button => {
    button.addEventListener('click', () => {
      const step = Number(button.dataset.videoThumbnailStep) || FRAME_STEP
      video.pause()
      const duration = Number.isFinite(video.duration) ? video.duration : Infinity
      video.currentTime = Math.min(Math.max(0, video.currentTime + step), duration)
      scrub.value = String(video.currentTime)
      updateTimeLabel()
    })
  })

  if (playButton) {
    playButton.addEventListener('click', () => {
      if (video.paused) {
        video.play().catch(error => {
          console.warn('Video thumbnail picker play failed:', error)
          showStatus('Playback failed — try scrubbing with the slider instead.')
        })
      } else {
        video.pause()
      }
    })
  }

  openButton.addEventListener('click', () => {
    if (sourceIndex === -1) {
      showStatus('Loading video…')
      loadSource(0)
    }
    panel.hidden = false
    openButton.hidden = true
  })

  if (cancelButton) {
    cancelButton.addEventListener('click', () => {
      video.pause()
      panel.hidden = true
      openButton.hidden = false
    })
  }

  function showPreview(file) {
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl)
    }
    previewObjectUrl = URL.createObjectURL(file)
    if (preview) {
      preview.src = previewObjectUrl
      preview.hidden = false
    }
    if (emptyLabel) {
      emptyLabel.hidden = true
    }
  }

  captureButton.addEventListener('click', async () => {
    if (!video.videoWidth || !video.videoHeight) {
      showStatus('The video has not loaded yet — give it a second and try again.')
      return
    }

    video.pause()

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    try {
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
      const blob = await canvasToBlob(canvas, 'image/jpeg', 0.92)
      if (!blob) {
        showStatus('Could not capture this frame. Try a different frame or upload an image instead.')
        return
      }

      const capturedAt = formatTime(video.currentTime)
      const file = new File([blob], 'event-video-thumbnail.jpg', { type: 'image/jpeg' })

      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      input.files = dataTransfer.files

      showPreview(file)
      showStatus(`Frame at ${capturedAt} selected — it uploads when you save the event.`)

      panel.hidden = true
      openButton.hidden = false
    } catch (error) {
      console.warn('Video thumbnail capture failed:', error)
      if (error && error.name === 'SecurityError') {
        showStatus('Your browser blocked frame capture from this video host. Take a screenshot of the frame and upload it as an image instead.')
      } else {
        showStatus('Could not capture this frame. Try a different frame or upload an image instead.')
      }
    }
  })

  // A manually chosen file also previews, and replaces any captured frame.
  input.addEventListener('change', () => {
    const [file] = input.files || []
    if (!file) return
    showPreview(file)
    showStatus('Image selected — it uploads when you save the event.')
  })
}

function init() {
  document.querySelectorAll('[data-video-thumbnail-picker]').forEach(setupPicker)
}

READY_EVENTS.forEach(eventName => {
  document.addEventListener(eventName, init)
})

if (document.readyState !== 'loading') {
  init()
}
